'use client';

// react
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Icon } from '@/components/atoms/Icon/Icon';
import { Icons } from '../Icon';

// mui
import { TextField, InputAdornment } from '@mui/material';

interface InputProps extends FieldProps<string> {
  className?: string;
  size?: 'sm' | 'md';
  iconLeft?: Icons;
  iconRight?: Icons;
  type?: 'password' | 'email' | 'text' | 'number';
  autoFocus?: boolean;
  minLength?: number;
  maxLength?: number;
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  isCentered?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * Input allows users to enter and edit text using MUI component.
 */
export const Input = ({
  id = 'input',
  className,
  label,
  description,
  iconLeft,
  iconRight,
  size = 'md',
  type = 'text',
  placeholder,
  ariaLabel,
  tooltip,
  value,
  onChange,
  validate = () => true,
  isDisabled = false,
  isRequired = false,
  isHideTitle = false,
  autoFocus = false,
  minLength,
  maxLength,
  onClick,
  isCentered,
  onKeyDown,
  ...props
}: InputProps) => {
  // state
  const [isError, setIsError] = useState(false);

  // effects
  useEffect(() => {
    setIsError(!validate(value) && (value?.length || 0) > 0);
  }, [value, validate]);

  return (
    <BaseField
      id={id}
      label={label}
      ariaLabel={ariaLabel}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <TextField
        className={className}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        helperText={description}
        placeholder={placeholder}
        error={isError}
        type={type}
        autoFocus={autoFocus}
        disabled={isDisabled}
        size={size === 'sm' ? 'small' : 'medium'}
        sx={{ width: '100%' }}
        onKeyDown={onKeyDown}
        onClick={onClick}
        slotProps={{
          input: {
            startAdornment: iconLeft ? (
              <InputAdornment position='start'>
                <Icon icon={iconLeft} fontSize={size === 'sm' ? 12 : 16} />
              </InputAdornment>
            ) : null,
            endAdornment: isError ? (
              <InputAdornment position='end'>
                <Box sx={{ color: '#e7294b' }}>
                  <Icon
                    icon='exclamation-circle'
                    fontSize={size === 'sm' ? 12 : 16}
                  />
                </Box>
              </InputAdornment>
            ) : iconRight ? (
              <InputAdornment position='end'>
                <Icon icon={iconRight} fontSize={size === 'sm' ? 12 : 16} />
              </InputAdornment>
            ) : null,
          },

          htmlInput: {
            maxLength: maxLength,
            minLength: minLength,
            'aria-label': ariaLabel || label,
          },
        }}
      />
    </BaseField>
  );
};

Input.componentName = 'Input';

export default Input;
